import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { PartnerCenterEnrollmentStatus } from "@gamedev-web/one-gaming-crm";

import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { DevKitOrders } from "../../components/DevKitOrders";
import { DevReadinessGuide } from "../../components/DevReadinessGuide";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { ScreenReaderHeading } from "../../components/ScreenReaderHeading";
import { ClarityEvents, trackClarityEvent } from "../../clarity";
import {
  enabledQueryParameterValue,
  partnerQueryParameters,
  partnerSectionLinks,
  xboxOnboardSiteLocationPrefix,
} from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { GameConceptFiltersForTab, GameConceptTab } from "../../helpers/gameConceptHelpers";
import { getPartnerDashboardEntryRedirect } from "../../helpers/partnerEntryNavigation";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import { gameConceptsListActions } from "../../redux/actions/gameConceptsListActions";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import { XboxOnboardApplicationStatus } from "../../server/crm/api/models/xboxOnboardModels";
import type { XboxOnboardEnrollmentEvidence } from "../../server/crm/api/models/xboxOnboardModels";
import { EmbargoedEnrollmentModal } from "./components/EmbargoedEnrollmentModal";
import { PartnerConceptSection } from "./components/PartnerConceptSection";
import { PartnerEnrollmentCard } from "./components/PartnerEnrollmentCard";
import { PartnerLifecycleBanners } from "./components/PartnerLifecycleBanners";
import { PartnerNdaCard } from "./components/PartnerNdaCard";
import { PartnerStudioCard } from "./components/PartnerStudioCard";
import { PartnerWelcomeModal } from "./components/PartnerWelcomeModal";
import {
  getAdditionalContractsBannerState,
  isPartnerCenterEnrollmentFinished,
  shouldShowAdditionalContractsBanner,
  shouldShowGameConceptRequiredBanner,
  shouldShowGameConceptReviewBanner,
} from "./partnerHomeBannerHelpers";
import * as styles from "./PartnerHomePage.module.css";

const PartnerHomePage = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.NdaPending);
  const isSignedIn = useRequireAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const data = useSelector(getXboxOnboardData);

  const searchParams = new URLSearchParams(location.search);
  const shouldStartOnboarding =
    searchParams.get(partnerQueryParameters.startOnboarding) === enabledQueryParameterValue;
  const [showWelcomeModal, setShowWelcomeModal] = useState(
    searchParams.get(partnerQueryParameters.welcome) === enabledQueryParameterValue
  );
  const [showSidebar, setShowSidebar] = useState(true);
  const [enrollmentModalAction, setEnrollmentModalAction] = useState<"start" | "retry">();
  const [accessDeniedTitle, accessDeniedMessage, dashboardLabel] = useStrings(
    "partnerHomeAccessDeniedTitle",
    "partnerHomeAccessDeniedMessage",
    "dashboardLabel"
  );
  const isFastTrackAccount = data?.isFastTrackAccount === true;
  const {
    needsTlaSignature,
    needsGdkSignature,
    messageKey: additionalContractsMessageKey,
  } = getAdditionalContractsBannerState({
    isFastTrackAccount,
    hasApprovedGameConcept: data?.hasApprovedGameConcept === true,
    hasGameConceptWithTlaReceived: data?.hasGameConceptWithTlaReceived === true,
    isGdkSigned: data?.isGdkSigned === true,
  });

  const handleCloseWelcomeModal = () => {
    trackClarityEvent(ClarityEvents.welcomeModalDismissed);
    setShowWelcomeModal(false);
    history.replace(`${xboxOnboardSiteLocationPrefix}/dashboard`);
  };

  useEffect(() => {
    if (showWelcomeModal) {
      trackClarityEvent(ClarityEvents.welcomeModalShown);
    }
  }, [showWelcomeModal]);

  useEffect(() => {
    if (isSignedIn) {
      dispatch(xboxOnboardActions.fetchData.start());
    }
  }, [dispatch, isSignedIn]);

  const entryRedirect = getPartnerDashboardEntryRedirect(
    data?.status,
    shouldStartOnboarding,
    data?.isPartnerCenterAccessDenied,
    data?.accessDeniedCode
  );

  useEffect(() => {
    if (entryRedirect) {
      history.replace(entryRedirect);
    }
  }, [entryRedirect, history]);

  // Start NDA polling when status is pending
  useEffect(() => {
    if (
      data?.status === XboxOnboardApplicationStatus.Submitted ||
      data?.status === XboxOnboardApplicationStatus.NdaSent ||
      data?.legalEntityNameChange?.hasActiveRequest === true
    ) {
      dispatch(xboxOnboardActions.pollNda.start());
    }
  }, [data?.legalEntityNameChange?.hasActiveRequest, data?.status, dispatch]);

  // Poll enrollment when in progress
  useEffect(() => {
    if (
      data?.partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.InProgress ||
      data?.partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.EarlyInProgress
    ) {
      dispatch(xboxOnboardActions.pollEnrollment.start());
    }
  }, [data?.partnerCenterEnrollmentStatus, dispatch]);

  const enrollmentStatus = data?.partnerCenterEnrollmentStatus;
  const isEnrollmentComplete =
    enrollmentStatus === PartnerCenterEnrollmentStatus.Complete ||
    enrollmentStatus === PartnerCenterEnrollmentStatus.EarlyComplete;
  const isEnrollmentEarlyComplete =
    enrollmentStatus === PartnerCenterEnrollmentStatus.EarlyComplete;
  const isEnrollmentRejected =
    enrollmentStatus === PartnerCenterEnrollmentStatus.Rejected ||
    enrollmentStatus === PartnerCenterEnrollmentStatus.EarlyRejected;

  // Treat rejected as "unblocked" — user should still access game concepts and other experiences
  const isEnrollmentFinished = isPartnerCenterEnrollmentFinished(enrollmentStatus);
  const isAaaUser = data?.hubType === "aaa";
  const isIdXboxApproved = data?.accountType === "id" && data?.isIdXboxApproved === true;
  const hasHubAccess = data?.hubPermissions.gameConcepts.view !== "none";
  const isNdaSigned = data?.status === XboxOnboardApplicationStatus.NdaSigned;
  const isNdaPending =
    data?.status === XboxOnboardApplicationStatus.Submitted ||
    data?.status === XboxOnboardApplicationStatus.NdaSent;
  const canShowDevReadinessGuide =
    (data?.isOnboardingHubProfileMode === true || isNdaPending || hasHubAccess) && !isAaaUser;
  const isDevReadinessGuideVisible = showSidebar && canShowDevReadinessGuide;

  // AAA users skip Partner Center enrollment — treat them as enrollment-finished
  const effectiveEnrollmentComplete = isEnrollmentComplete || isAaaUser;
  const effectiveEnrollmentFinished = isEnrollmentFinished || isAaaUser;

  // Fetch game concepts when enrollment is complete or rejected (don't block user)
  useEffect(() => {
    if (effectiveEnrollmentFinished) {
      dispatch(
        gameConceptsListActions.fetchGameConcepts.start({
          currentPage: 1,
          orderBy: "",
          filterBy: GameConceptFiltersForTab[GameConceptTab.All],
          searchBy: "",
        })
      );
    }
  }, [effectiveEnrollmentFinished, dispatch]);

  const handleEnrollmentAction = (action: "start" | "retry") => {
    if (data?.isEmbargoedCountry) {
      setEnrollmentModalAction(action);
      return;
    }

    dispatch(
      action === "start"
        ? xboxOnboardActions.startEnrollment.start()
        : xboxOnboardActions.retryEnrollment.start()
    );
  };

  const handleEmbargoedEnrollment = (evidence: XboxOnboardEnrollmentEvidence) => {
    dispatch(
      enrollmentModalAction === "retry"
        ? xboxOnboardActions.retryEnrollment.start(evidence)
        : xboxOnboardActions.startEnrollment.start(evidence)
    );
    setEnrollmentModalAction(undefined);
  };

  const isDashboardVisible = Boolean(isPageReady && isSignedIn && data && !entryRedirect);

  useEffect(() => {
    if (isDashboardVisible) {
      trackClarityEvent(ClarityEvents.dashboardViewed);
    }
  }, [isDashboardVisible]);

  if (!isPageReady || !isSignedIn || !data || entryRedirect) {
    return (
      <PartnerPageLayout>
        <div role="status" aria-live="polite" aria-busy="true">
          <PageLoadingSpinner />
        </div>
      </PartnerPageLayout>
    );
  }

  if (data?.hubType === "none") {
    return (
      <PartnerPageLayout>
        <div className={styles.accessDenied}>
          <h1>{accessDeniedTitle}</h1>
          <p>{accessDeniedMessage}</p>
        </div>
      </PartnerPageLayout>
    );
  }

  const isLegalEntityNameChangeNdaRecipient =
    (data.legalEntityNameChange?.isApprovedAwaitingNda || data.legalEntityNameChange?.hasNdaSent) &&
    data.legalEntityNameChange.ndaRecipientEmail?.trim().toLowerCase() ===
      data.contact.email.trim().toLowerCase();
  const legalEntityNameChange =
    data.hubPermissions.canAccessAdministratorSettings || isLegalEntityNameChangeNdaRecipient
      ? data.legalEntityNameChange
      : undefined;
  const hasLegalEntityNameChangeNdaPending = legalEntityNameChange?.hasNdaSent === true;
  const showEnrollmentLegalEntityNameChangeStatus =
    legalEntityNameChange?.hasActiveRequest === true &&
    !hasLegalEntityNameChangeNdaPending &&
    isNdaSigned &&
    !effectiveEnrollmentComplete &&
    data.hubPermissions.canSubmitPCEnrollment;

  const isEnrollmentInProgress =
    enrollmentStatus === PartnerCenterEnrollmentStatus.InProgress ||
    enrollmentStatus === PartnerCenterEnrollmentStatus.EarlyInProgress;

  // Show the "Apply to ID@Xbox" banner under the studio header whenever the user is not
  // ID@Xbox-approved (no onboarding entity, or a decision other than Approved). ID@Xbox does
  // not apply to AAA users, and the studio header only exists once the NDA is signed. Hide it
  // while the user still needs to enroll in Partner Center (enrollment enabled/pending) or while
  // an enrollment is currently processing.
  const showApplyToIdXbox =
    !isAaaUser &&
    !isNdaPending &&
    !isEnrollmentInProgress &&
    !isIdXboxApproved &&
    enrollmentStatus !== PartnerCenterEnrollmentStatus.Enabled;
  const hasStartedIdXboxApplication = data?.hasStartedIdXboxApplication === true;
  const showAdditionalContractsBanner = shouldShowAdditionalContractsBanner({
    isNdaSigned,
    isEnrollmentFinished: effectiveEnrollmentFinished,
    isIdXboxApproved,
    isFastTrackAccount,
    hasApprovedGameConcept: data?.hasApprovedGameConcept === true,
    needsTlaSignature,
    needsGdkSignature,
  });
  const showGameConceptRequiredBanner = shouldShowGameConceptRequiredBanner({
    isNdaSigned,
    isEnrollmentFinished: effectiveEnrollmentFinished,
    isIdXboxApproved,
    isFastTrackAccount,
    hasGameConceptWithTlaReceived: data?.hasGameConceptWithTlaReceived === true,
    hasSubmittedGameConcept: data?.hasSubmittedGameConcept === true,
  });
  const showGameConceptReviewBanner = shouldShowGameConceptReviewBanner({
    isNdaSigned,
    isEnrollmentFinished: effectiveEnrollmentFinished,
    isIdXboxApproved,
    isFastTrackAccount,
    hasGameConceptUnderReview: data?.hasGameConceptUnderReview === true,
    hasApprovedGameConcept: data?.hasApprovedGameConcept === true,
    hasGameConceptWithTlaReceived: data?.hasGameConceptWithTlaReceived === true,
  });
  const hasLifecycleBanner =
    showApplyToIdXbox ||
    showAdditionalContractsBanner ||
    showGameConceptRequiredBanner ||
    showGameConceptReviewBanner;
  const showNdaCard = hasLegalEntityNameChangeNdaPending || (isNdaPending && !isAaaUser);
  const ndaCardManagePath = data.isOnboardingHubProfileMode
    ? partnerSectionLinks.agreements.to
    : partnerSectionLinks.overview.to;
  const showNdaCardSettings = !hasLegalEntityNameChangeNdaPending || isNdaSigned || isAaaUser;

  return (
    <>
      <PartnerWelcomeModal isOpen={showWelcomeModal} onClose={handleCloseWelcomeModal} />
      <EmbargoedEnrollmentModal
        isOpen={Boolean(enrollmentModalAction)}
        initialDunsValue={data.dunsNumber}
        onClose={() => setEnrollmentModalAction(undefined)}
        onSubmit={handleEmbargoedEnrollment}
      />
      <PartnerPageLayout>
        <div
          className={`${isDevReadinessGuideVisible ? styles.content : styles.contentFull}${
            effectiveEnrollmentFinished ? "" : ` ${styles.withBackground}`
          }`}
        >
          <div className={styles.mainColumn}>
            <ScreenReaderHeading>{dashboardLabel}</ScreenReaderHeading>
            {showNdaCard ? (
              <PartnerNdaCard
                contactEmail={data?.contact?.email}
                ndaSentDate={data?.ndaSentDate}
                ndaNumber={data?.ndaNumber}
                hasNdaId={data.isOnboardingHubProfileMode ? data.hasNdaId : undefined}
                legalEntityNameChange={
                  hasLegalEntityNameChangeNdaPending ? legalEntityNameChange : undefined
                }
                hasAttachedBanner={hasLifecycleBanner}
                managePath={ndaCardManagePath}
                showAgreementsOption={!isAaaUser}
                showDevReadinessGuideOption={canShowDevReadinessGuide && !showSidebar}
                showSettingsOption={showNdaCardSettings}
                onShowDevReadinessGuide={() => setShowSidebar(true)}
              />
            ) : isNdaSigned &&
              !effectiveEnrollmentComplete &&
              data.hubPermissions.canSubmitPCEnrollment ? (
              <PartnerEnrollmentCard
                studioName={data?.studio?.studioName}
                sellerId={data?.sellerID}
                isIdXboxApproved={isIdXboxApproved}
                enrollmentStatus={enrollmentStatus}
                isEnrollmentRejected={isEnrollmentRejected}
                isEnrollmentInProgress={isEnrollmentInProgress}
                isDunsRequired={data?.isEmbargoedCountry === true && !data?.dunsNumber}
                legalEntityNameChange={
                  showEnrollmentLegalEntityNameChangeStatus ? legalEntityNameChange : undefined
                }
                contactEmail={data.contact.email}
                hasAttachedBanner={hasLifecycleBanner}
                showDevReadinessGuideOption={canShowDevReadinessGuide && !showSidebar}
                onShowDevReadinessGuide={() => setShowSidebar(true)}
                onStartEnrollment={() => handleEnrollmentAction("start")}
                onRetryEnrollment={() => handleEnrollmentAction("retry")}
              />
            ) : (
              <PartnerStudioCard
                studioName={data?.studio?.studioName}
                sellerId={data?.sellerID}
                legalEntityNameChange={legalEntityNameChange}
                contactEmail={data?.contact?.email}
                isIdXboxApproved={isIdXboxApproved}
                isEnrollmentComplete={isEnrollmentComplete}
                isEnrollmentEarlyComplete={isEnrollmentEarlyComplete}
                showAgreementsOption={!isAaaUser}
                hasAttachedBanner={hasLifecycleBanner}
                showDevReadinessGuideOption={canShowDevReadinessGuide && !showSidebar}
                showSettingsOption={isNdaSigned || isAaaUser}
                onShowDevReadinessGuide={() => setShowSidebar(true)}
              />
            )}

            <PartnerLifecycleBanners
              showGameConceptRequired={showGameConceptRequiredBanner}
              showGameConceptReview={showGameConceptReviewBanner}
              showAdditionalContracts={showAdditionalContractsBanner}
              showApplyToIdXbox={showApplyToIdXbox}
              hasStartedIdXboxApplication={hasStartedIdXboxApplication}
              canSubmitIdApplication={data.hubPermissions.canSubmitIdApplication}
              additionalContractsMessageKey={additionalContractsMessageKey}
            />

            {!!data?.ndaSignedDate && effectiveEnrollmentFinished && <DevKitOrders />}

            {effectiveEnrollmentFinished && <PartnerConceptSection isAaaUser={isAaaUser} />}
          </div>

          {isDevReadinessGuideVisible && (
            <DevReadinessGuide
              email={data?.contact?.email}
              ndaSignedDate={data?.ndaSignedDate}
              ndaSentDate={data?.ndaSentDate}
              showIdXboxOnboardingLink={isEnrollmentFinished}
              isIdXboxApproved={isIdXboxApproved}
              canSubmitIdApplication={data.hubPermissions.canSubmitIdApplication}
              onClose={() => setShowSidebar(false)}
            />
          )}
        </div>
      </PartnerPageLayout>
    </>
  );
};

export default PartnerHomePage;
